import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import { colors, typography, spacing, sizing, focusVisible } from '@/styles/designTokens'
// Ensures the twoCol breakpoint augmentation is in scope for theme.breakpoints.up('twoCol')
import '@/styles/theme'

export const SearchResultsHeader = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.lg,
	paddingTop: spacing.lg,
	paddingBottom: spacing.lg,
	paddingLeft: spacing.lg,
	paddingRight: spacing.lg,
	borderBottom: `1px solid ${colors.divider}`,
})

export const SearchResultsTitle = styled('p')({
	flex: '1 0 0',
	margin: 0,
	fontSize: typography.fontSizeLg,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingNormal,
	color: colors.textPrimary,
})

export const ClearSearchButton = styled('button')({
	flexShrink: 0,
	padding: 0,
	border: 'none',
	backgroundColor: 'transparent',
	cursor: 'pointer',
	fontFamily: 'inherit',
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingTight,
	color: colors.textSecondary,
	whiteSpace: 'nowrap',
	'&:hover': { color: colors.textPrimary },
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.overlayHeavy}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const EmptySearchState = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: spacing.xs,
	width: '100%',
	minHeight: sizing.emptySearchMinHeight,
	paddingTop: spacing.xxl,
	paddingBottom: spacing.xxl,
	paddingLeft: spacing.lg,
	paddingRight: spacing.lg,
})

export const EmptySearchTitle = styled('p')({
	width: '100%',
	margin: 0,
	textAlign: 'center',
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
})

export const EmptySearchHint = styled('p')({
	width: '100%',
	margin: 0,
	textAlign: 'center',
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	color: colors.textSecondary,
})

export const CategoryHeadingRow = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.lg,
	marginBottom: spacing.lg,
})

export const CategoryName = styled('h2')(({ theme }) => ({
	flex: '1 0 0',
	margin: 0,
	fontSize: typography.fontSizeLg,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingNormal,
	color: colors.textPrimary,
	[theme.breakpoints.up('lg')]: {
		fontSize: typography.fontSizeXl,
		lineHeight: typography.lineHeightXl,
		letterSpacing: typography.letterSpacingNormalLg,
	},
}))

export const CategoryResultCount = styled('span')({
	flexShrink: 0,
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textSecondary,
	whiteSpace: 'nowrap',
})

export const ItemGrid = styled(Box)({
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'flex-start',
	gap: spacing.lg,
})

export const CategorySection = styled('section')(({ theme }) => ({
	marginBottom: spacing.xxl,
	paddingTop: spacing.xl,
	paddingLeft: spacing.lg,
	paddingRight: spacing.lg,
	// Offset scroll-to-section by the sticky header height so the category heading
	// lands below the nav bar rather than being obscured by it
	scrollMarginTop: sizing.stickyHeaderScrollOffset,
	[theme.breakpoints.up('md')]: {
		maxWidth: sizing.tabletContentMaxWidth,
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	[theme.breakpoints.up('lg')]: {
		// At lg the LayoutGrid already provides 48px horizontal padding — remove the
		// section-level inner padding and width cap so items fill the menu column.
		maxWidth: 'none',
		marginLeft: 0,
		marginRight: 0,
		paddingLeft: 0,
		paddingRight: 0,
	},
}))

// twoCol = 640px — two columns as soon as the container fits 2×280px cards side-by-side
export const ItemSlot = styled(Box)(({ theme }) => ({
	flex: '1 0 100%',
	maxWidth: '100%',
	minWidth: 280,
	[theme.breakpoints.up('twoCol')]: {
		flex: `1 0 calc(50% - ${spacing.sm}px)`,
		maxWidth: `calc(50% - ${spacing.sm}px)`,
	},
	[theme.breakpoints.up('lg')]: {
		minWidth: sizing.menuItemMinWidth,
	},
}))
